/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import type {
  AdvisedProduct,
  Integration,
  SqueezelyConfig,
  UnionOfType,
} from "../../../../app/types"
import type { BaseEvent, Product } from "../../types"
import type {
  AdvisorInfo,
  SessionContext,
  UsageAnalyticsEventTransformer,
} from "../types"

type SqueezelyBase = UnionOfType<BaseEvent & { integration: "aiden:squeezely" }>

type SqueezelyAnsweredEvent = UnionOfType<
  SqueezelyBase & {
    eventData: {
      event: string
      custom_aiden_app_id: string
      custom_aiden_app_name: string
      custom_aiden_question: string
      custom_aiden_answers: string[]
    }
  }
>

type SqueezelyAdvisedEvent = UnionOfType<
  SqueezelyBase & {
    eventData: {
      event: string
      custom_aiden_app_id: string
      custom_aiden_app_name: string
      products: { id: string; name: string }[]
    }
  }
>
type SqueezelyCustomerInfoEvent = UnionOfType<
  SqueezelyBase & {
    eventData: {
      event: string
      email: string
      custom_aiden_app_id: string
      custom_aiden_checkbox: boolean
      custom_aiden_app_name: string
      products: { id: string; name: string }[]
    }
  }
>
type SqueezelyInfoPageCustomerInfoEvent = UnionOfType<
  SqueezelyBase & {
    eventData: {
      event: string
      email: string
      custom_aiden_app_id: string
      custom_aiden_checkbox: boolean
      custom_aiden_app_name: string
      custom_aiden_identifier: string
    }
  }
>
type SqueezelyNoResultsCustomerInfoEvent = UnionOfType<
  SqueezelyBase & {
    eventData: {
      event: string
      email: string
      custom_aiden_app_id: string
      custom_aiden_checkbox: boolean
      custom_aiden_app_name: string
    }
  }
>
type SqueezelyEmailOptInEvent = UnionOfType<
  SqueezelyBase & {
    eventData: {
      event: "EmailOptIn"
      email: string
      newsletter: "yes"
      custom_aiden_app_id: string
      custom_aiden_app_name: string
    }
  }
>

type SqueezelyAddToCartEvent = UnionOfType<
  SqueezelyBase & {
    eventData: {
      event: string
      custom_aiden_app_id: string
      custom_aiden_app_name: string
      products: { id: string; name: string; quantity: number | undefined }[]
    }
  }
>

const notAllowedCharactersRegex = /[^a-zA-Z ()./:+\-_]/g
function normalizeAdvisorName(name: string) {
  return name.replace(notAllowedCharactersRegex, "")
}

/**
 *
 * Handles the creation of usage events specific to Spotler Active (formerly Squeezely)
 *
 * */
export const SqueezelyUsageEvents = (
  config: SqueezelyConfig
): UsageAnalyticsEventTransformer => {
  return {
    questionAnswered: (
      advisor,
      context,
      integrations,
      question,
      answers
    ): SqueezelyAnsweredEvent[] => {
      return [
        {
          advisorId: advisor.id,
          advisorName: advisor.name,
          integration: "aiden:squeezely",
          eventData: {
            event: "Aiden answer: " + normalizeAdvisorName(advisor.name),
            custom_aiden_app_id: advisor.id,
            custom_aiden_app_name: advisor.name,
            custom_aiden_question: question.title,
            custom_aiden_answers: answers,
          },
        },
      ]
    },

    adviceGiven: (
      advisor,
      context,
      integrations,
      products
    ): SqueezelyAdvisedEvent[] => {
      return [
        {
          advisorId: advisor.id,
          advisorName: advisor.name,
          integration: "aiden:squeezely",
          eventData: {
            event: "Aiden advice: " + normalizeAdvisorName(advisor.name),
            custom_aiden_app_id: advisor.id,
            custom_aiden_app_name: advisor.name,
            products: formatProducts(config, products),
          },
        },
      ]
    },

    customerInfoSubmitted: (
      advisor,
      context,
      integrations,
      isPreview,
      info: {
        email: string
        checkboxChecked: boolean
        advisedProducts: AdvisedProduct[]
      }
    ): (SqueezelyCustomerInfoEvent | SqueezelyEmailOptInEvent)[] => {
      if (isPreview) return []
      if (!info.advisedProducts) return []
      const base = makeBaseEvent(advisor)
      const eventData = makeSqueezelyEventData(
        "Aiden followup",
        advisor,
        info.email,
        info.checkboxChecked
      )
      const infoEvent: SqueezelyCustomerInfoEvent = {
        ...base,
        eventData: {
          ...eventData,
          products: formatProducts(
            config,
            info.advisedProducts.map((p) => {
              return {
                clientProductId: p.id,
                data: p.data,
                id: p.id,
                name: p.name,
                image: p.imageUrl,
                link: p.url,
              }
            })
          ),
        },
      }
      if (info.checkboxChecked)
        return [infoEvent, makeOptInEvent(advisor, info.email)]

      return [infoEvent]
    },

    infoPageCustomerInfoSubmitted: (
      advisor: AdvisorInfo,
      context,
      integrations,
      isPreview,
      info: {
        email: string
        checkboxChecked: boolean
        formIdentifier: string
      }
    ): (SqueezelyInfoPageCustomerInfoEvent | SqueezelyEmailOptInEvent)[] => {
      if (isPreview) return []
      const base = makeBaseEvent(advisor)
      const eventData = makeSqueezelyEventData(
        "Aiden info page followup",
        advisor,
        info.email,
        info.checkboxChecked
      )
      const infoEvent: SqueezelyInfoPageCustomerInfoEvent = {
        ...base,
        eventData: {
          ...eventData,
          custom_aiden_identifier: info.formIdentifier,
        },
      }
      if (info.checkboxChecked)
        return [infoEvent, makeOptInEvent(advisor, info.email)]

      return [infoEvent]
    },

    noResultsCustomerInfoSubmitted: (
      advisor: AdvisorInfo,
      context,
      integrations,
      isPreview,
      info: {
        email: string
        checkboxChecked: boolean
      }
    ): (SqueezelyNoResultsCustomerInfoEvent | SqueezelyEmailOptInEvent)[] => {
      if (isPreview) return []
      const base = makeBaseEvent(advisor)
      const eventData = makeSqueezelyEventData(
        "Aiden no results followup",
        advisor,
        info.email,
        info.checkboxChecked
      )
      const infoEvent: SqueezelyNoResultsCustomerInfoEvent = {
        ...base,
        eventData: eventData,
      }

      if (info.checkboxChecked)
        return [infoEvent, makeOptInEvent(advisor, info.email)]

      return [infoEvent]
    },

    //
    // These events don't have a usecase (yet)
    //
    clickedOnProduct: () => [],
    conversationStarted: () => [],
  }
}

function makeSqueezelyEventData(
  eventPrefix: string,
  advisor: AdvisorInfo,
  email: string,
  checkboxChecked: boolean
) {
  return {
    event: eventPrefix + ": " + normalizeAdvisorName(advisor.advisorName),
    email: email,
    custom_aiden_app_id: advisor.id,
    custom_aiden_app_name: advisor.name,
    custom_aiden_checkbox: checkboxChecked,
  }
}

function makeOptInEvent(
  advisor: AdvisorInfo,
  email: string
): SqueezelyEmailOptInEvent {
  return {
    ...makeBaseEvent(advisor),
    eventData: {
      event: "EmailOptIn",
      email: email,
      newsletter: "yes",
      custom_aiden_app_id: advisor.id,
      custom_aiden_app_name: advisor.name,
    },
  }
}

function makeBaseEvent(advisor: AdvisorInfo): {
  advisorId: string
  advisorName: string
  integration: "aiden:squeezely"
} {
  return {
    advisorId: advisor.id,
    advisorName: advisor.name,
    integration: "aiden:squeezely",
  }
}

/**
 * Which product data to use as the ID sent to Squeezely can be configured.
 * This function replaces the clientProductId with the value of the configured field.
 */
function withConfiguredProductId(
  config: SqueezelyConfig,
  product: Product
): Product {
  if (typeof config.idField === "string") {
    return {
      ...product,
      clientProductId:
        replacementToString(product.data[config.idField]) ||
        product.clientProductId,
    }
  }
  return product
}

function replacementToString(replacement: unknown): string | null {
  if (Array.isArray(replacement)) {
    const first = replacement[0]
    if (!first) return null
    return replacementToString(first)
  }
  if (typeof replacement === "string") {
    return replacement
  }
  if (typeof replacement === "number") {
    return `${replacement}`
  }
  return null
}

function formatProducts(
  config: SqueezelyConfig,
  products: Product[]
): { id: string; name: string }[] {
  return products
    .map((product) => withConfiguredProductId(config, product))
    .map((product) => ({
      id: product.clientProductId,
      name: product.name,
    }))
}

function formatQuantityProducts(
  config: SqueezelyConfig,
  products: Product[]
): { id: string; name: string; quantity: number | undefined }[] {
  return products
    .map((product) => withConfiguredProductId(config, product))
    .map((product) => ({
      id: product.clientProductId,
      name: product.name,
      quantity: product.quantity,
    }))
}
